import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, Brain, Activity, BadgeCheck, ShieldCheck, Waves, Droplets, Pill, Syringe, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import { StoryCarousel } from "@/components/StoryCarousel";
import useSEO from "@/hooks/useSEO";
import { useLang } from "@/context/LangContext";

const HERO = "https://images.unsplash.com/photo-1666214282459-c7dff167ecc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtZWRpY2FsJTIwaG9zcGl0YWwlMjB0ZWNobm9sb2d5JTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3Nzg3NDM1MzJ8MA&ixlib=rb-4.1.0&q=85";

const indications = [
  { title: "Treatment-Resistant Depression", desc: "For patients who have not responded to two or more antidepressant trials." },
  { title: "Obsessive Compulsive Disorder", desc: "FDA-approved deep TMS protocols for moderate-severe OCD." },
  { title: "Anxiety & Panic Disorders", desc: "Targeted protocols for generalised anxiety and panic." },
  { title: "Post-Traumatic Stress", desc: "Emerging evidence and structured PTSD protocols." },
  { title: "Smoking Cessation", desc: "Approved short-course rTMS to reduce nicotine cravings." },
  { title: "Migraine & Chronic Pain", desc: "Adjunct neuromodulation for treatment-refractory cases." },
];

const why = [
  { icon: Zap, label: "Non-invasive", desc: "No anesthesia, no surgery. You walk in and walk out." },
  { icon: Activity, label: "Drug-free", desc: "An alternative or adjunct to medication, with no systemic side-effects." },
  { icon: BadgeCheck, label: "FDA-Approved", desc: "Backed by global clinical evidence for multiple indications." },
  { icon: ShieldCheck, label: "Tolerated Well", desc: "Most patients return to work the same day after a 30-min session." },
];

export default function Neuromodulation() {
  useSEO({
    title: "rTMS Saurashtra · Neuromodulation Suite — TMS, tDCS, Ketamine IV",
    description: "Interventional neuromodulation at Manas Hospital, Bhavnagar: rTMS, modified Brief Pulse Therapy, tDCS, Ketamine IV infusion, Naltrexone implants. TMS Physician trained at AIIMS, New Delhi.",
    image: HERO,
    lang: "en",
    alternates: [
      { hreflang: "en-IN", href: "https://manasneuropsychiatryhospital.in/neuromodulation" },
      { hreflang: "gu-IN", href: "https://manasneuropsychiatryhospital.in/gu/neuromodulation" },
      { hreflang: "x-default", href: "https://manasneuropsychiatryhospital.in/neuromodulation" },
    ],
  });
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  return (
    <div data-testid="neuromodulation-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <p className="eyebrow text-[#A39171] mb-6">{t("Interventional Neuromodulation")}</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-5xl">
          {t("The brain is plastic.")} <br /><span className="italic text-[#E07A5F]">{t("So is hope.")}</span>
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-2xl leading-relaxed">
          {t("A dedicated suite for advanced neuromodulation — rTMS, modified Brief Pulse Therapy, tDCS, ketamine IV infusion, and naltrexone implants — to help patients whose conditions have not responded to medication alone.")}
        </p>
        <Button data-testid="nm-hero-book" onClick={() => setOpen(true)} className="mt-10 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
          {t("Discuss Eligibility")} <ArrowUpRight className="ml-1 w-4 h-4" />
        </Button>
        <div>
          <Link
            to="/gu/neuromodulation"
            data-testid="nm-switch-gujarati"
            className="mt-6 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
          >
            <Languages className="w-4 h-4" /> ગુજરાતીમાં વાંચો
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={HERO} alt="Neuromodulation device" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2D2623]/60 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-md text-white">
            <p className="eyebrow text-[#E2C044] mb-3">{t("rTMS Suite")}</p>
            <p className="font-display text-2xl md:text-3xl leading-tight">{t("Figure-of-eight coil technology with the latest stimulation protocols.")}</p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">{t("Therapies We Offer")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("Five advanced modalities,")} <span className="italic text-[#E07A5F]">{t("one calm suite")}</span>.
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
              {t("Our interventional unit offers a complete toolkit for treatment-resistant neuropsychiatric and addictive disorders.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {/* rTMS */}
            <div data-testid="therapy-rtms" className="md:col-span-7 bg-[#F4EFE6] rounded-3xl p-8 md:p-10 border border-[#A39171]/15 group hover:shadow-[0_20px_50px_rgba(163,145,113,0.15)] transition-shadow">
              <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-5">
                <Brain className="w-5 h-5" />
              </span>
              <p className="eyebrow text-[#A39171] mb-2">{t("Most evidence · FDA-approved")}</p>
              <h3 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight">
                {t("Repetitive Transcranial Magnetic Stimulation (rTMS)")}
              </h3>
              <p className="text-[#5C524E] mt-4 leading-relaxed">
                {t("Focused magnetic pulses gently stimulate specific brain regions. Non-invasive, no anaesthesia, no systemic side-effects. Approved for treatment-resistant depression, OCD, anxiety, smoking cessation and more. A typical course is 4–6 weeks of daily 30-minute sessions.")}
              </p>
              <Link to="/therapies/rtms" data-testid="nm-guide-rtms" className="mt-6 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium text-sm link-underline">
                {t("Read the full rTMS guide")} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* mBPT */}
            <div data-testid="therapy-mbpt" className="md:col-span-5 bg-[#F4EFE6] rounded-3xl p-8 md:p-10 border border-[#A39171]/15 group hover:shadow-[0_20px_50px_rgba(163,145,113,0.15)] transition-shadow">
              <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-5">
                <Zap className="w-5 h-5" />
              </span>
              <p className="eyebrow text-[#A39171] mb-2">{t("Modified · Brief Pulse")}</p>
              <h3 className="font-display text-3xl text-[#2D2623] tracking-tight">
                {t("Modified Brief Pulse Therapy (mBPT)")}
              </h3>
              <p className="text-[#5C524E] mt-4 leading-relaxed">
                {t("A modern, modified form of brief-pulse electroconvulsive therapy administered under full anaesthesia with continuous monitoring. Reserved for severe, life-threatening depression, catatonia, and treatment-resistant psychosis where rapid response is critical.")}
              </p>
              <Link to="/therapies/mbpt" data-testid="nm-guide-mbpt" className="mt-6 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium text-sm link-underline">
                {t("Read the full mBPT guide")} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* tDCS */}
            <div data-testid="therapy-tdcs" className="md:col-span-5 bg-[#F4EFE6] rounded-3xl p-8 md:p-10 border border-[#A39171]/15 group hover:shadow-[0_20px_50px_rgba(163,145,113,0.15)] transition-shadow">
              <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-5">
                <Waves className="w-5 h-5" />
              </span>
              <p className="eyebrow text-[#A39171] mb-2">{t("Low-intensity · Take-home capable")}</p>
              <h3 className="font-display text-3xl text-[#2D2623] tracking-tight">
                {t("Transcranial Direct Current Stimulation (tDCS)")}
              </h3>
              <p className="text-[#5C524E] mt-4 leading-relaxed">
                {t("A gentle, low-intensity direct current is delivered through scalp electrodes to modulate cortical excitability. Well-tolerated, painless, and increasingly evidence-based for depression, cognitive rehabilitation post brain injury, and chronic pain.")}
              </p>
              <Link to="/therapies/tdcs" data-testid="nm-guide-tdcs" className="mt-6 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium text-sm link-underline">
                {t("Read the full tDCS guide")} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Ketamine IV */}
            <div data-testid="therapy-ketamine" className="md:col-span-7 bg-[#F4EFE6] rounded-3xl p-8 md:p-10 border border-[#A39171]/15 group hover:shadow-[0_20px_50px_rgba(163,145,113,0.15)] transition-shadow">
              <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-5">
                <Droplets className="w-5 h-5" />
              </span>
              <p className="eyebrow text-[#A39171] mb-2">{t("Rapid-acting · Closely monitored")}</p>
              <h3 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight">
                {t("Ketamine IV Infusion Therapy")}
              </h3>
              <p className="text-[#5C524E] mt-4 leading-relaxed">
                {t("Sub-anaesthetic intravenous ketamine, delivered slowly under continuous medical monitoring, can produce rapid relief in treatment-resistant depression and active suicidal ideation — often within hours. Administered in a calm, supervised setting with a structured maintenance schedule.")}
              </p>
              <Link to="/therapies/ketamine-infusion" data-testid="nm-guide-ketamine" className="mt-6 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium text-sm link-underline">
                {t("Read the full ketamine infusion guide")} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Naltrexone */}
            <div data-testid="therapy-naltrexone" className="md:col-span-12 bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-12 relative overflow-hidden group">
              <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-8">
                  <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-5">
                    <Pill className="w-5 h-5" />
                  </span>
                  <p className="eyebrow text-[#E2C044] mb-2">{t("Deaddiction · Long-acting")}</p>
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
                    {t("Naltrexone Drug Implants for Deaddiction")}
                  </h3>
                  <p className="text-[#FDFBF7]/80 mt-5 leading-relaxed">
                    {t("A small, slow-release naltrexone implant placed under the skin provides a steady blockade of opioid and alcohol cravings for several months — eliminating the daily pill burden, dramatically improving adherence, and protecting recovery during the most vulnerable post-detox phase.")}
                  </p>
                  <Link to="/therapies/naltrexone-implant" data-testid="nm-guide-naltrexone" className="mt-6 inline-flex items-center gap-2 text-[#E2C044] hover:text-[#FDFBF7] font-medium text-sm link-underline">
                    {t("Read the full naltrexone implant guide")} <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="md:col-span-4">
                  <div className="bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 rounded-2xl p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <Syringe className="w-5 h-5 text-[#E2C044] mt-0.5" />
                      <p className="text-sm text-[#FDFBF7]/80">{t("Indicated for")}</p>
                    </div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2" /> {t("Alcohol Use Disorder")}</li>
                      <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2" /> {t("Opioid Dependence")}</li>
                      <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2" /> {t("Post-detox relapse prevention")}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("Why neuromodulation")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            {t("A different door,")} <span className="italic text-[#E07A5F]">{t("when the others have closed")}</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {why.map((w, i) => (
              <div key={w.label} data-testid={`nm-why-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-7 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-5">
                  <w.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-xl text-[#2D2623]">{t(w.label)}</h3>
                <p className="text-[#5C524E] mt-2 text-sm">{t(w.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("Indications")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            {t("Conditions our rTMS programme treats.")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {indications.map((ind, i) => (
              <div key={ind.title} data-testid={`nm-ind-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15 group hover:shadow-[0_20px_50px_rgba(163,145,113,0.12)] transition-all">
                <Brain className="w-7 h-7 text-[#E07A5F] mb-5 group-hover:scale-110 transition-transform" />
                <h3 className="font-display text-2xl text-[#2D2623] tracking-tight">{t(ind.title)}</h3>
                <p className="text-[#5C524E] mt-2">{t(ind.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("The Treatment Journey")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14">{t("A typical rTMS course.")}</h2>
          <ol className="space-y-10">
            {[
              { t: "Day 0 — Evaluation", d: "Comprehensive psychiatric review, eligibility & contraindication screening." },
              { t: "Day 1 — Mapping", d: "Motor threshold determination to personalise stimulation intensity." },
              { t: "Weeks 1–4 — Acute Course", d: "Daily 30-min sessions, 5 days a week. Most patients return to work after each visit." },
              { t: "Weeks 5–6 — Taper & Review", d: "Stimulation tapered, response assessed, maintenance plan crafted." },
            ].map((s, i) => (
              <li key={s.t} data-testid={`nm-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3"><span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span></div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight">{t(s.t)}</h3>
                  <p className="text-[#5C524E] mt-3 text-lg">{t(s.d)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[#F4EFE6]/40 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <StoryCarousel
            category="neuromodulation"
            eyebrow={t("What patients report")}
            heading={
              <>
                {t("When medicines had stalled,")} <span className="italic text-[#E07A5F]">{t("this is what changed")}</span>.
              </>
            }
            note={t("Anonymous accounts from patients treated in our neuromodulation suite — rTMS, tDCS and ketamine infusion. All identifying details have been removed.")}
          />
        </div>
      </section>

      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
            {t("Curious if you're a candidate?")} <span className="italic text-[#E07A5F]">{t("Let's talk")}</span>.
          </h2>
          <p className="text-[#FDFBF7]/70 mt-5 max-w-xl mx-auto">
            {t("A 30-minute eligibility consultation. Honest answers. No pressure.")}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="nm-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">{t("Book Consultation")}</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">{t("Call")} +91 94275 60101</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
